/**
 * Ledger page (platform.ledger.list).
 *
 * Read-only query over the uo_balance_log hypertable. Filters:
 *   - user_id (exact)
 *   - asset (one of TOKEN_IDS)
 *   - action (e.g. recharge, admin_adjust, daily_settlement, ep_trade)
 *   - date range (start_date, end_date)
 *
 * Pagination is server-side via DataTable. All decimal values are rendered
 * as strings to avoid JS Number precision loss.
 */
import { useState } from 'react';
import { DatePicker, Input, Segmented, Select, Space, Tag } from 'antd';
import type { Dayjs } from 'dayjs';
import { DataTable } from '../../components/DataTable';
import { TOKEN_IDS, type TokenId } from '@uo/shared';

interface LedgerRow {
  id: string;
  user_id: number;
  asset: string;
  action: string;
  delta: string;
  balance_before: string;
  balance_after: string;
  ref: string | null;
  operator_id: number | null;
  note: string | null;
  create_time: string;
}

const ACTION_COLORS: Record<string, string> = {
  recharge: 'green',
  withdraw: 'volcano',
  admin_adjust: 'purple',
  ep_trade: 'blue',
  daily_settlement: 'geekblue',
  referral_bonus: 'cyan',
  transfer_out: 'orange',
  transfer_in: 'lime',
  withdraw_fee: 'magenta',
};

export type LedgerPageVariant = 'full' | 'transfer' | 'withdraw';

export interface LedgerPageProps {
  /** `transfer` — P2P 转账流水；`withdraw` — 提现/手续费账本（无独立提现表时的审计视图）。 */
  variant?: LedgerPageVariant;
}

export function LedgerPage({ variant = 'full' }: LedgerPageProps = {}) {
  const [userId, setUserId] = useState<string>('');
  const [asset, setAsset] = useState<string | undefined>();
  const [action, setAction] = useState<string | undefined>();
  const [transferLeg, setTransferLeg] = useState<'all' | 'out' | 'in'>('all');
  const [withdrawLeg, setWithdrawLeg] = useState<'all' | 'body' | 'fee'>('all');
  const [range, setRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const transferActions =
    transferLeg === 'all'
      ? 'transfer_out,transfer_in'
      : transferLeg === 'out'
        ? 'transfer_out'
        : 'transfer_in';

  const withdrawActions =
    withdrawLeg === 'all'
      ? 'withdraw,withdraw_fee'
      : withdrawLeg === 'body'
        ? 'withdraw'
        : 'withdraw_fee';

  const columns = [
    { title: '流水号', dataIndex: 'id', width: 120 },
    { title: '时间', dataIndex: 'create_time', width: 170 },
    { title: '用户 ID', dataIndex: 'user_id', width: 90 },
    { title: '资产', dataIndex: 'asset', width: 90, render: (v: string) => v.toUpperCase() },
    {
      title: '动作',
      dataIndex: 'action',
      width: 150,
      render: (v: string) => <Tag color={ACTION_COLORS[v] ?? 'default'}>{v}</Tag>,
    },
    {
      title: '变动',
      dataIndex: 'delta',
      width: 160,
      render: (v: string) => (
        <span style={{ color: v.startsWith('-') ? '#ff4d4f' : '#52c41a', fontFamily: 'monospace' }}>
          {v}
        </span>
      ),
    },
    { title: '变动前', dataIndex: 'balance_before', width: 160 },
    { title: '变动后', dataIndex: 'balance_after', width: 160 },
    { title: '关联 ref', dataIndex: 'ref' },
    { title: '操作员', dataIndex: 'operator_id', width: 90 },
    { title: '备注', dataIndex: 'note' },
  ];

  const isTransfer = variant === 'transfer';
  const isWithdraw = variant === 'withdraw';

  return (
    <div>
      {isTransfer && <h2 style={{ marginTop: 0 }}>转账记录</h2>}
      {isWithdraw && <h2 style={{ marginTop: 0 }}>提现记录</h2>}
      {isTransfer && (
        <div style={{ marginBottom: 12 }}>
          <Segmented
            value={transferLeg}
            onChange={(v) => setTransferLeg(v as 'all' | 'out' | 'in')}
            options={[
              { label: '全部', value: 'all' },
              { label: '转出', value: 'out' },
              { label: '转入', value: 'in' },
            ]}
          />
        </div>
      )}
      {isWithdraw && (
        <div style={{ marginBottom: 12 }}>
          <Segmented
            value={withdrawLeg}
            onChange={(v) => setWithdrawLeg(v as 'all' | 'body' | 'fee')}
            options={[
              { label: '全部', value: 'all' },
              { label: '提现', value: 'body' },
              { label: '手续费', value: 'fee' },
            ]}
          />
        </div>
      )}
      <Space style={{ marginBottom: 12 }} wrap>
        <Input
          placeholder="用户 ID"
          style={{ width: 140 }}
          allowClear
          onChange={(e) => setUserId(e.target.value)}
        />
        <Select
          placeholder="资产"
          style={{ width: 140 }}
          allowClear
          onChange={setAsset}
          options={TOKEN_IDS.map((t: TokenId) => ({ value: t, label: t.toUpperCase() }))}
        />
        {!isTransfer && !isWithdraw && (
          <Select
            placeholder="动作类型"
            style={{ width: 180 }}
            allowClear
            onChange={setAction}
            options={Object.keys(ACTION_COLORS).map((a) => ({ value: a, label: a }))}
          />
        )}
        <DatePicker.RangePicker
          onChange={(v) => setRange(v as [Dayjs | null, Dayjs | null] | null)}
        />
      </Space>

      <DataTable<LedgerRow>
        queryKey={[
          'admin',
          'ledger',
          variant,
          isTransfer ? transferLeg : isWithdraw ? withdrawLeg : action ?? '',
        ]}
        endpoint="/api/admin/ledger"
        extraParams={{
          user_id: userId || undefined,
          asset,
          ...(isTransfer
            ? { actions: transferActions }
            : isWithdraw
              ? { actions: withdrawActions }
              : { action }),
          start_date: range?.[0]?.format('YYYY-MM-DD'),
          end_date: range?.[1]?.format('YYYY-MM-DD'),
        }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1600 }}
      />
    </div>
  );
}
